/**
 * Shared helpers (WIL-461 dedupe). Loaded first in theme.liquid <head> with
 * defer, so every other deferred script can rely on window.MapUtils.
 *
 * One source of truth for:
 * - vehicleShopUrl — the SEO-critical contract §2 path-filter URL
 * - extractPostcode — AU 4-digit postcode from free text
 * - gstRateFromDataset / formatMoney — GST-inclusive money, matching
 *   gst-money.liquid / shop.money_format
 * - readCookie / setCookie
 * - partNumberTagQuery — exact `tag:apgpartno_<number>` query (WIL-746).
 *   A bare tag is split on hyphens, so the field prefix is required.
 *   Storefront `OR` against that tag matches the whole catalogue, so callers
 *   search the tag on its own.
 */
(function () {
  /**
   * Vehicle path-filter URL (contract §2): /collections/all/+make_X+model_Y…
   * Values are percent-encoded verbatim — vehicle tags contain real spaces, so
   * slugifying them (spaces → '_') produced tags that never match.
   * @param {Record<string, string | undefined> | null} vehicle
   */
  function vehicleShopUrl(vehicle) {
    const segments = [];
    if (vehicle?.make) segments.push(`+make_${encodeURIComponent(String(vehicle.make).trim())}`);
    if (vehicle?.model) segments.push(`+model_${encodeURIComponent(String(vehicle.model).trim())}`);
    if (vehicle?.year) segments.push(`+year_${encodeURIComponent(String(vehicle.year).trim())}`);
    if (vehicle?.series) segments.push(`+series_${encodeURIComponent(String(vehicle.series).trim())}`);
    if (!segments.length) return '/collections/all';
    return `/collections/all/${segments.join('')}`;
  }

  /** @param {string} value */
  function extractPostcode(value) {
    const match = String(value || '').match(/\b(\d{4})\b/);
    return match ? match[1] : '';
  }

  /**
   * GST multiplier from data-gst-enabled / data-gst-percent.
   * @param {DOMStringMap} dataset
   */
  function gstRateFromDataset(dataset) {
    if (dataset.gstEnabled === 'false') return 1;
    const percent = Number.parseFloat(dataset.gstPercent || '10');
    if (!Number.isFinite(percent) || percent < 0) return 1;
    return 1 + percent / 100;
  }

  /**
   * @param {number} value
   * @param {string} thousands
   */
  function groupThousands(value, thousands) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  }

  /**
   * GST-inclusive money, matching gst-money.liquid / shop.money_format.
   * @param {number} cents
   * @param {number} gstRate - multiplier from gstRateFromDataset (1 = no GST)
   * @param {string} [moneyFormat] - shop.money_format (default '${{amount}}')
   */
  function formatMoney(cents, gstRate, moneyFormat) {
    const taxed = Math.round(cents * (Number.isFinite(gstRate) && gstRate > 0 ? gstRate : 1));
    const whole = Math.floor(taxed / 100);
    const decimals = String(taxed % 100).padStart(2, '0');
    const format = moneyFormat || '${{amount}}';
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, `${groupThousands(whole, ',')}.${decimals}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, groupThousands(Math.round(taxed / 100), ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${groupThousands(whole, '.')},${decimals}`);
  }

  /**
   * Decoded cookie value, or '' when absent. Falls back to the raw value when
   * decodeURIComponent throws on a malformed cookie.
   * @param {string} name
   */
  function readCookie(name) {
    const prefix = `${name}=`;
    const entry = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));
    if (!entry) return '';

    try {
      return decodeURIComponent(entry.slice(prefix.length));
    } catch {
      return entry.slice(prefix.length);
    }
  }

  /**
   * APG part numbers are stored as the tag `apgpartno_<number>`. A bare number
   * does not match that tag, and a bare tag query is split on hyphens, so the
   * returned query uses the exact `tag:` field. Returns '' when `keyword` is a
   * word or phrase. Requires a digit so "headlamp" is left alone.
   * Joining this to the typed query with OR matches every tagged product, so
   * callers must search the tag by itself.
   * @param {string} keyword
   */
  function partNumberTagQuery(keyword) {
    const term = String(keyword || '').trim();
    if (term.length < 3 || /^(?:tag:)?apgpartno_/i.test(term)) return '';
    if (!/^[A-Za-z0-9\-_]+$/.test(term) || !/\d/.test(term)) return '';
    return `tag:apgpartno_${term}`;
  }

  /** @param {string} name @param {string} value @param {number} [days] */
  function setCookie(name, value, days = 365) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  window.MapUtils = {
    vehicleShopUrl,
    extractPostcode,
    gstRateFromDataset,
    formatMoney,
    readCookie,
    setCookie,
    partNumberTagQuery,
  };
})();
